// src/oura/components/visibility/CitesDrawer.tsx
//
// One quoted site, and the questions whose answers quote it. The payload does
// not list those questions yet, and the drawer then says so rather than
// guessing them from the count. A question opened from here keeps the site in
// the URL, so closing it comes back to this drawer.

import { Dot, Drawer, DrawerBlock, ListRow, PillButton, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { countedUnit, countedUnitLabel } from "../../lib/oura";
import type { VisModel, VisRow, VisSourceRow } from "../../visibility/model";
import { SOURCE_KIND, sourceKind } from "./sourceKinds";
import { sourceRowControl } from "./rowFocus";

export function CitesDrawer({
    source,
    model,
    staff,
    onClose,
    onOpenQuestion,
}: {
    source: VisSourceRow | null;
    model: VisModel;
    staff: boolean;
    onClose: () => void;
    onOpenQuestion: (row: VisRow) => void;
}) {
    // Unmounted rather than closed, so the focus lookup the drawer runs a
    // frame after closing still knows which row it came from.
    if (!source) return null;
    const basis = model.sources.basis;
    const kind = sourceKind(source);
    const kindLabel = kind ? (kind === "own" ? (staff ? "The business's own site" : "Your site") : SOURCE_KIND[kind].label) : null;
    // Out of the headline questions, as the backend counts sources: branded
    // and unwinnable questions are not in it.
    const of = model.headline.of;
    const count = countedUnit(basis) === "question"
        ? of > 0
            ? `quoted on ${source.count} of the ${of} questions`
            : `quoted on ${source.count} ${source.count === 1 ? "question" : "questions"}`
        : `quoted in ${source.count} ${countedUnitLabel(source.count, basis)}`;
    const rows = source.questions
        ? source.questions
            .map((n) => model.rows.find((r) => r.id === n))
            .filter((r): r is VisRow => r !== undefined)
        : null;

    return (
        <Drawer
            key={source.domain}
            open
            onClose={onClose}
            returnFocus={() => sourceRowControl(source.domain)}
            title={source.domain}
            sub={[kindLabel, count].filter(Boolean).join(" · ")}
            foot={<PillButton onClick={onClose}>Close</PillButton>}
        >
            {rows ? (
                <DrawerBlock label="Answers that quote it">
                    <div className="mt-1">
                        {rows.map((r) => (
                            <ListRow key={r.id} icon={<Dot tone={r.named ? "green" : "gray"} />} title={r.text} onClick={() => onOpenQuestion(r)} />
                        ))}
                    </div>
                </DrawerBlock>
            ) : (
                <DrawerBlock>
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>The questions behind this count are not listed on this scan.</p>
                </DrawerBlock>
            )}
        </Drawer>
    );
}
